import { Injectable, OnDestroy, inject } from "@angular/core";
import { assign, isNil } from "lodash";
import { BehaviorSubject, Subject } from "rxjs";
import { filter, map, switchMap, takeUntil } from "rxjs/operators";
import { CreateVirkeskostnaderCommand } from "../commands/virkeskostnader/CreateVirkeskostnaderCommand";
import { DeleteVirkeskostnaderCommand } from "../commands/virkeskostnader/DeleteVirkeskostnaderCommand";
import { UpdateVirkeskostnaderCommand } from "../commands/virkeskostnader/UpdateVirkeskostnaderCommand";
import { Virkeskostnad, VirkeskostnadEvent, Virkeskostnader, VirkeskostnaderEvent } from "../models/virkeskostnader/virkeskostnader-models";
import { KontraktCommandService } from "./kontrakt-command.service";
import { KontraktMediatorService } from "./kontrakt-mediator.service";
import { VirkeskostnaderService } from "./virkeskostnader.service";

@Injectable()
export class VirkeskostnaderMediatorService implements OnDestroy {
    virkeskostnader$ = new BehaviorSubject<VirkeskostnaderEvent>(null);

    private readonly _virkeskostnadCreatedSubject$ = new Subject<VirkeskostnadEvent>();
    private readonly _virkeskostnadDeletedSubject$ = new Subject<VirkeskostnadEvent>();
    private readonly _virkeskostnadUpdatedSubject$ = new Subject<VirkeskostnadEvent>();
    private readonly destroy$ = new Subject<void>();
    private readonly kontraktCommandService = inject(KontraktCommandService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly virkeskostnaderService = inject(VirkeskostnaderService);

    private _virkeskostnader: Virkeskostnader;

    constructor() {
        this._virkeskostnader = new Virkeskostnader();
        const kostnader$ = this.kontraktMediatorService.kontrakt$.pipe(
            map((k) => k.id),
            filter((id) => !isNil(id)),
            switchMap(kontraktId => this.virkeskostnaderService.getVirkeskostnaderForKontrakt(kontraktId)),
            takeUntil(this.destroy$)
        );
        kostnader$.subscribe(
            {
                next: vk => {
                    this._virkeskostnader = vk;
                    this.notify();
                },
                error: (error) => {
                    console.error(error);
                }
            }
        );
    }

    get virkeskostnadCreated$() { return this._virkeskostnadCreatedSubject$.asObservable(); }

    get virkeskostnadDeleted$() { return this._virkeskostnadDeletedSubject$.asObservable(); }

    get virkeskostnadUpdated$() { return this._virkeskostnadUpdatedSubject$.asObservable(); }

    create(kontraktGUID: string, atgardGUID: string, rad: Virkeskostnad, source?: any): void {
        this.kontraktCommandService.update(
            new CreateVirkeskostnaderCommand(rad.guid, kontraktGUID, atgardGUID, rad.mattslag, rad.handelssortimentId, rad.handelssortimentsgruppId));
        this._virkeskostnadCreatedSubject$.next(new VirkeskostnadEvent(kontraktGUID, atgardGUID, rad, source));
        this.createOrUpdate(rad, atgardGUID);
        this.notify(source);
    }

    delete(kontraktGUID: string, atgardGUID: string, rad: Virkeskostnad, source?: any): void {
        this.kontraktCommandService.update(new DeleteVirkeskostnaderCommand(rad.guid, atgardGUID, kontraktGUID));
        this._virkeskostnadDeletedSubject$.next(new VirkeskostnadEvent(kontraktGUID, atgardGUID, rad, source));
        this.removeKostnad(rad.guid, atgardGUID);
        this.notify(source);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    update(kontraktGUID: string, atgardGUID: string, rad: Virkeskostnad, source?: any): void {
        this.kontraktCommandService.update(new UpdateVirkeskostnaderCommand(rad, atgardGUID, kontraktGUID));
        this._virkeskostnadUpdatedSubject$.next(new VirkeskostnadEvent(kontraktGUID, atgardGUID, rad, source));
        this.createOrUpdate(rad, atgardGUID);
        this.notify(source);
    }

    volymForAtgard(atgardGUID: string): number {
        const atgard = this._virkeskostnader.getForAtgard(atgardGUID);
        return atgard?.virkeskostnader
            .map(p => p.kvantitet * p.konverteringsfaktorHandelssortimentMattslag)
            .reduce((prev, curr) => prev + curr, 0) ?? 0;
    }

    private createOrUpdate(value: Virkeskostnad, atgardGUID?: string) {
        let kostnad;
        if (isNil(atgardGUID)) {
            kostnad = this._virkeskostnader
                .getOrCreateVirkeskostnadForKontrakt(value.guid);
        } else {
            kostnad = this._virkeskostnader
                .getOrCreateVirkeskostnaderForAtgard(atgardGUID)
                .getOrCreateVirkeskostnad(value.guid);
        }
        assign(kostnad, value);
    }

    private notify(source?: any) {
        this.virkeskostnader$.next(new VirkeskostnaderEvent(this._virkeskostnader, source));
    }

    private removeKostnad(virkeskostnadGUID: string, atgardGUID?: string) {
        if (isNil(atgardGUID)) {
            const kostnadIdx = this._virkeskostnader.kontraktVirkeskostnader.findIndex(x => x.guid === virkeskostnadGUID);
            if (kostnadIdx > -1) {
                this._virkeskostnader.kontraktVirkeskostnader.splice(kostnadIdx, 1);
            }
        } else {
            const atgard = this._virkeskostnader.atgarderVirkeskostnader.find(x => x.virkeskopsatgardGUID === atgardGUID);
            if (!isNil(atgard)) {
                const kostnadIdx = atgard.virkeskostnader.findIndex(x => x.guid === virkeskostnadGUID);
                if (kostnadIdx > -1) {
                    atgard.virkeskostnader.splice(kostnadIdx, 1);
                }
            }
        }
    }
}
